import React, { useState } from 'react';
import { AdminSection, ShowMore, useShowMore, writeAdminRoute } from '../components/AdminNav.jsx';

// Parts both Veridion tabs show the same way: the MCP servers with their tools and errors, the
// orchestrator's tools, the usage ledger and the activity feed. Tables of one kind share one fixed
// column grid, so stacked tables keep their column edges.

export const humanizeToken = (value) => String(value || '-')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

// Medal terminology for local GPU chat engines (IDD D-HP-ADMIN-054): gold-llm and silver-llm replace raw
// Qwen*-gold / Qwen*-silver model names in visible dashboard labels, while keeping the routed value for API requests.
export const displayModelName = (model) => {
  const value = String(model || '');
  if (!value) return value;
  if (/-gold$/i.test(value) || value.toLowerCase() === 'gold' || value.toLowerCase() === 'gold-llm') return 'gold-llm';
  if (/-silver$/i.test(value) || value.toLowerCase() === 'silver' || value.toLowerCase() === 'silver-llm') return 'silver-llm';
  if (/^qwen.*27b$/i.test(value)) return 'silver-llm';
  return value;
};

const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
// Money in the reader's locale with two decimals, like the token counts beside it; a cost below one cent is
// shown as such instead of as zero.
const usd = (value) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
export const formatUsd = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  return amount > 0 && amount < 0.01 ? `< ${usd(0.01)}` : usd(amount);
};

// Every time the dashboard shows is Berlin time, whatever the viewer's browser zone (IDD D-HP-ADMIN-039). A value
// may be an ISO string with or without a zone, an epoch in seconds or milliseconds, or a Date; a timestamp without
// a zone is UTC, as every runtime of the platform writes it. A plain date and a value that is no instant are
// returned as they are.
const BERLIN = 'Europe/Berlin';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const toInstant = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value < 1e12 ? value * 1000 : value);
  let text = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(text)) return toInstant(Number(text));
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) text = `${text.replace(' ', 'T')}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};
const berlinParts = (date) => Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
  timeZone: BERLIN, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
}).formatToParts(date).map((part) => [part.type, part.value]));
export const formatBerlin = (value, { seconds = true } = {}) => {
  if (value === null || value === undefined || value === '') return '-';
  if (DATE_ONLY.test(String(value).trim())) return String(value).trim();
  const date = toInstant(value);
  if (!date) return String(value);
  const p = berlinParts(date);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}${seconds ? `:${p.second}` : ''}`;
};
export const formatBerlinTime = (value) => {
  const date = toInstant(value);
  if (!date) return value ? String(value) : '-';
  const p = berlinParts(date);
  return `${p.hour}:${p.minute}:${p.second}`;
};
export const formatBerlinDay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (DATE_ONLY.test(String(value).trim())) return String(value).trim();
  const date = toInstant(value);
  if (!date) return String(value);
  const p = berlinParts(date);
  return `${p.year}-${p.month}-${p.day}`;
};
// Midnight of the current Berlin day as an instant.
export const startOfBerlinDay = (now = new Date()) => {
  const p = berlinParts(now);
  const guess = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day));
  const q = berlinParts(new Date(guess));
  const offsetMs = Date.UTC(Number(q.year), Number(q.month) - 1, Number(q.day), Number(q.hour), Number(q.minute), Number(q.second)) - guess;
  return new Date(guess - offsetMs);
};

export const McpServerTable = ({ servers, t, formatTimestamp }) => {
  const [open, setOpen] = useState('');
  const o = t.admin.ops || {};
  const stateLabel = (status) => ({
    connected: t.admin.veridionMcpConnected,
    unreachable: t.admin.veridionMcpUnreachable,
    disabled: t.admin.veridionMcpDisabled,
  }[status] || status || '-');
  const stateClass = (status) => (status === 'connected' ? 'is-ok' : status === 'unreachable' ? 'is-missing' : 'is-neutral');
  return (
    <div className="admin-table-shell">
      <div className="admin-table-scroll">
        <table className="admin-table admin-fixed-table">
          <colgroup><col style={{ width: '30%' }} /><col style={{ width: '14%' }} /><col style={{ width: '10%' }} /><col style={{ width: '18%' }} /><col style={{ width: '14%' }} /><col style={{ width: '14%' }} /></colgroup>
          <thead>
            <tr>
              <th>{t.admin.veridionMcpServer}</th>
              <th>{t.admin.veridionMcpState}</th>
              <th>{t.admin.veridionMcpTools}</th>
              <th>{t.admin.veridionMcpLastSuccess}</th>
              <th>{o.mcpVersion}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {servers.map((server) => (
              <React.Fragment key={server.serverId}>
                <tr>
                  <td>
                    <div className="font-semibold text-text-primary admin-cell-clip" title={server.displayName || server.serverId}>{server.displayName || server.serverId}</div>
                    {server.description ? <div className="text-xs text-text-muted">{server.description}</div> : null}
                  </td>
                  <td><span className={`admin-status-pill ${stateClass(server.status)}`}>{stateLabel(server.status)}</span></td>
                  <td>{server.toolCount ?? '-'}</td>
                  <td>{formatTimestamp(server.lastSuccess)}</td>
                  <td className="admin-cell-clip" title={server.version || ''}>{server.version || '-'}</td>
                  <td>
                    <button type="button" className="homepage-button-secondary" onClick={() => setOpen(open === server.serverId ? '' : server.serverId)}>
                      {open === server.serverId ? o.hideDetails : o.showDetails}
                    </button>
                  </td>
                </tr>
                {open === server.serverId ? (
                  <tr className="admin-detail-row">
                    <td colSpan="6">
                      <div className="admin-detail-grid">
                        <div><span className="admin-kpi-label">{o.mcpUrl}</span><div className="break-all">{server.url || '-'}</div></div>
                        <div><span className="admin-kpi-label">{o.mcpProtocol}</span><div>{[server.type, server.protocolVersion].filter(Boolean).join(' · ') || '-'}</div></div>
                        <div><span className="admin-kpi-label">{o.mcpLastRefresh}</span><div>{formatTimestamp(server.lastRefresh)}</div></div>
                      </div>
                      {server.error ? <div className="text-sm text-error mt-2">{o.mcpError}: {server.error}</div> : null}
                      <div className="admin-smtm-issue-list mt-2">
                        {(server.toolNames || []).map((name) => <span key={name} className="admin-status-pill is-neutral">{name}</span>)}
                        {!(server.toolNames || []).length ? <span className="text-sm text-text-secondary">{o.noTools}</span> : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
            {!servers.length ? <tr><td colSpan="6">{t.admin.veridionMcpEmpty}</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// The orchestrator's registered tools with where they come from, how they run and whether they write.
export const ToolTable = ({ tools, t }) => {
  const o = t.admin.ops || {};
  const access = (tool) => {
    if (tool.destructiveHint) return o.accessDestructive;
    if (tool.readOnlyHint) return o.accessReadOnly;
    return tool.isExternal ? o.accessUndeclared : '-';
  };
  return (
    <div className="admin-table-shell">
      <div className="admin-table-scroll">
        <table className="admin-table admin-fixed-table">
          <colgroup><col style={{ width: '34%' }} /><col style={{ width: '18%' }} /><col style={{ width: '14%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '12%' }} /></colgroup>
          <thead>
            <tr>
              <th>{t.admin.veridionToolName}</th>
              <th>{o.toolServer}</th>
              <th>{t.admin.veridionToolRuntime}</th>
              <th>{t.admin.veridionToolRisk}</th>
              <th>{o.toolAccess}</th>
              <th>{t.admin.veridionToolEnabled}</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((entry) => (
              <tr key={entry.name}>
                <td>
                  <div className="font-semibold text-text-primary admin-cell-clip" title={entry.title || entry.name}>{entry.title || entry.name}</div>
                  <div className="text-xs text-text-muted break-all">{entry.name}</div>
                </td>
                <td className="admin-cell-clip" title={entry.externalServerName || ''}>{entry.isExternal ? (entry.externalServerName || entry.externalServerId || '-') : o.toolBuiltIn}</td>
                <td>{entry.runtime || '-'}</td>
                <td>{entry.riskLevel || '-'}</td>
                <td>{access(entry)}</td>
                <td><span className={`admin-status-pill ${entry.effectiveEnabled ? 'is-ok' : 'is-neutral'}`}>{entry.effectiveEnabled ? t.admin.statusConfigured : t.admin.statusMissing}</span></td>
              </tr>
            ))}
            {!tools.length ? <tr><td colSpan="6">{t.admin.veridionNoTools}</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LedgerTable = ({ title, nameLabel, rows, t, empty }) => {
  const o = t.admin.ops || {};
  const [shown, more] = useShowMore(30);  // long ledgers page (D-HP-ADMIN-040)
  return (
    <div className="space-y-2">
      <h3 className="font-black">{title}</h3>
      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '40%' }} /><col style={{ width: '15%' }} /><col style={{ width: '20%' }} /><col style={{ width: '25%' }} /></colgroup>
            <thead>
              <tr><th>{nameLabel}</th><th>{t.admin.companyUsageCalls}</th><th>{t.admin.companyUsageTokens}</th><th>{t.admin.companyUsageCost}</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, shown).map(([name, entry]) => {
                const label = displayModelName(name) || name;
                return (
                  <tr key={name}>
                    <td className="admin-cell-clip" title={label}>{label}</td>
                    <td>{formatInteger(entry.requests)}</td>
                    <td>{formatInteger(entry.totalTokens)}</td>
                    <td>{entry.costReportedRequests ? formatUsd(entry.costUsd) : t.admin.companyCostNotReported}</td>
                  </tr>
                );
              })}
              {!rows.length ? <tr><td colSpan="4">{empty || o.noUsage}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
      <ShowMore shown={shown} total={rows.length} onMore={more} />
    </div>
  );
};

// What OpenRouter itself bills for the platform's key: the truth of the company's model spend. Rendered inside the
// one section that carries it (IDD D-HP-ADMIN-040: OpenRouter's billing stands once, on the usage page of the company
// instance).
export const OpenRouterPanel = ({ openrouter, error, t }) => (
  <div className="space-y-2">
    {openrouter ? (
      <FactGrid items={[
        [t.admin.openrouterRemaining, `${formatUsd(openrouter.remaining)} ${t.admin.openrouterOf} ${formatUsd(openrouter.credits)}`],
        [t.admin.openrouterToday, formatUsd(openrouter.today)],
        [t.admin.openrouterWeek, formatUsd(openrouter.week)],
        [t.admin.openrouterMonth, formatUsd(openrouter.month)],
      ]} />
    ) : <p className="text-sm text-error">{t.admin.openrouterUnavailable}{error ? ` (${error})` : ''}</p>}
    <p className="text-xs text-text-muted">{t.admin.openrouterNote}</p>
  </div>
);

// The company's GPU (D-HP-ADMIN-025, D-HP-ADMIN-027), on the one page that carries the engines (D-HP-ADMIN-040): every
// engine of the card with its live figures, the assignment of who is served by which engine at which rank, the calls
// of Veridion Core in flight, the tokens taken from the card per identity in the running window with the budgets per
// identity and engine, and the form that sets such a budget. Every other page links here instead of repeating a figure.
// The engine of a local model name (IDD D-SAAS-086): gold serves the main bot, the autopilot, the agents, the
// architect and the orchestrator; silver serves the AI colleagues, the background services, research, the knowledge
// bases, the pipeline review and Veridion SMTM.
const engineOf = (model) => (/(-gold$|^gold(-llm)?$)/i.test(model || '') ? 'gold' : /(-silver$|^silver(-llm)?$)/i.test(model || '') ? 'silver' : '');
const engineLabel = (t, engine) => (engine === 'gold' ? t.admin.gpuEngineGold : engine === 'silver' ? t.admin.gpuEngineSilver : engine);
const whoLabel = (t, who) => t.admin[`gpuWho${(who || '').charAt(0).toUpperCase()}${(who || '').slice(1)}`] || who;

// Every engine the admin API read from the card: chat engines and the speech engines alike, one row each.
const engineRows = (gpu) => (gpu?.engines || []).flatMap((engine) => (engine.available && engine.models?.length
  ? engine.models.map((row) => ({ backend: engine.name, model: row.model, engine: engineOf(row.model), live: row, idle: false, available: true }))
  : [{ backend: engine.name, model: '', engine: '', live: null, idle: Boolean(engine.available), available: Boolean(engine.available) }]));

export const GpuPanel = ({ gpu, error, t, onBudget = null, saving = false, labels = {} }) => {
  const [identity, setIdentity] = useState('');
  const [tokens, setTokens] = useState('');
  const [budgetModel, setBudgetModel] = useState('');
  const n = labels;
  const rows = engineRows(gpu);
  const model = budgetModel || (gpu?.models || [])[0] || '';
  const budgetOf = (name, forModel) => (gpu?.budgets || []).find((rule) => rule.identity === name && rule.model === forModel);
  const chosen = identity || (gpu?.known_identities || [])[0] || '';
  const running = rows.reduce((sum, row) => sum + Number(row.live?.running || 0), 0);
  const waiting = rows.reduce((sum, row) => sum + Number(row.live?.waiting || 0), 0);
  const confirmRemove = (what) => window.confirm(String(n.confirmRemove || '{what}?').replace('{what}', what));
  if (!gpu) {
    return (
      <AdminSection id="envoy-engines" title={t.admin.aiGatewayEngines} tone="error" state={`${t.admin.gpuUnavailable}${error ? ` (${error})` : ''}`}>
        <p className="text-xs text-text-muted">{t.admin.gpuNote}</p>
      </AdminSection>
    );
  }
  const identityRows = (gpu.identities || []).map((entry) => ({ identity: entry.identity, model: entry.model, tokens: entry.tokens, budget: budgetOf(entry.identity, entry.model) }))
    .concat((gpu.budgets || []).filter((rule) => !(gpu.identities || []).some((entry) => entry.identity === rule.identity && entry.model === rule.model))
      .map((rule) => ({ identity: rule.identity, model: rule.model, tokens: 0, budget: rule })));
  return (
    <>
      <AdminSection id="envoy-engines" title={t.admin.aiGatewayEngines} subtitle={t.admin.aiGatewayEnginesHint}
        state={`${t.admin.aiEngineRunning}: ${formatInteger(running)} · ${t.admin.aiEngineWaiting}: ${formatInteger(waiting)}`} count={rows.length}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '16%' }} /><col style={{ width: '16%' }} /><col style={{ width: '24%' }} /><col style={{ width: '11%' }} /><col style={{ width: '11%' }} /><col style={{ width: '11%' }} /><col style={{ width: '11%' }} /></colgroup>
            <thead><tr><th>{t.admin.aiGatewayBackend}</th><th>{t.admin.gpuEngine}</th><th>{t.admin.aiGatewayModel}</th><th>{t.admin.aiEngineRunning}</th><th>{t.admin.aiEngineWaiting}</th><th>{t.admin.aiEngineCache}</th><th>{t.admin.aiEngineServed}</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.backend}-${row.model}`}>
                  <td className="admin-user-name admin-cell-clip" title={row.backend}>{row.backend}</td>
                  <td>{row.engine ? engineLabel(t, row.engine) : '-'}</td>
                  <td className="admin-cell-clip" title={displayModelName(row.model)}>{displayModelName(row.model) || '-'}</td>
                  {row.live ? (
                    <>
                      <td>{formatInteger(row.live.running)}</td>
                      <td>{formatInteger(row.live.waiting)}</td>
                      <td>{`${Math.round((row.live.kv_cache_usage || 0) * 100)} %`}</td>
                      <td>{formatInteger((row.live.prompt_tokens || 0) + (row.live.generation_tokens || 0))}</td>
                    </>
                  ) : <td colSpan="4" className="text-text-muted">{row.idle ? t.admin.aiEngineIdle : t.admin.aiEngineUnavailable}</td>}
                </tr>
              ))}
              {!rows.length ? <tr><td colSpan="7">{t.admin.aiEngineUnavailable}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
        <FactGrid items={[
          [t.admin.gpuTokensWindow, formatInteger(gpu.tokens_window)],
          [t.admin.aiGatewayWindow, gpu.window || '-'],
          [t.admin.aiEngineServed, t.admin.gpuSinceStart],
        ]} />
        <p className="text-xs text-text-muted">{t.admin.gpuNote}</p>
      </AdminSection>

      <AdminSection id="envoy-lanes" title={t.admin.gpuLanes} count={(gpu.lanes || []).length}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '40%' }} /><col style={{ width: '30%' }} /><col style={{ width: '30%' }} /></colgroup>
            <thead><tr><th>{t.admin.gpuEngine}</th><th>{t.admin.gpuRank}</th><th>{t.admin.gpuInFlight}</th></tr></thead>
            <tbody>
              {(gpu.lanes || []).map((lane) => (
                <tr key={`${lane.engine}-${lane.rank}`}><td>{engineLabel(t, lane.engine)}</td><td>{lane.rank}</td><td>{formatInteger(lane.inFlight)}</td></tr>
              ))}
              {!(gpu.lanes || []).length ? <tr><td colSpan="3">0</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </AdminSection>

      <AdminSection id="envoy-assignment" title={t.admin.gpuAssignment} subtitle={t.admin.gpuRankHint} count={(gpu.assignment || []).length} collapsed>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '28%' }} /><col style={{ width: '58%' }} /><col style={{ width: '14%' }} /></colgroup>
            <thead><tr><th>{t.admin.gpuEngine}</th><th>{t.admin.gpuWho}</th><th>{t.admin.gpuRank}</th></tr></thead>
            <tbody>
              {(gpu.assignment || []).map((row) => (
                <tr key={`${row.engine}-${row.who}`}><td>{engineLabel(t, row.engine)}</td><td>{whoLabel(t, row.who)}</td><td>{row.rank}</td></tr>
              ))}
              {!(gpu.assignment || []).length ? <tr><td colSpan="3">{t.admin.gpuNoneYet}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </AdminSection>

      <AdminSection id="envoy-gpu-identities" title={t.admin.gpuIdentities} count={identityRows.length}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '34%' }} /><col style={{ width: '22%' }} /><col style={{ width: '14%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /></colgroup>
            <thead><tr><th>{t.admin.aiGatewayIdentity}</th><th>{t.admin.aiGatewayModel}</th><th>{t.admin.core?.tokens || 'Tokens'}</th><th>{t.admin.aiGatewayTokenBudget}</th><th></th></tr></thead>
            <tbody>
              {identityRows.map((entry) => (
                <tr key={`${entry.identity}-${entry.model}`}>
                  <td className="admin-cell-clip" title={entry.identity}>{entry.identity}</td>
                  <td className="admin-cell-clip" title={displayModelName(entry.model)}>{displayModelName(entry.model)}</td>
                  <td>{formatInteger(entry.tokens)}</td>
                  <td>{entry.budget ? `${formatInteger(entry.budget.tokens)} / ${entry.budget.window}` : t.admin.aiBudgetUnlimited}</td>
                  <td>{entry.budget && onBudget ? (
                    <button type="button" className="homepage-button-secondary is-destructive" disabled={saving}
                      onClick={() => confirmRemove(`${t.admin.aiGatewayTokenBudget} ${entry.identity} · ${displayModelName(entry.model)}`) && onBudget({ scope: 'identity', identity: entry.identity, model: entry.model, remove: true })}>
                      {t.admin.aiBudgetRemove}
                    </button>
                  ) : null}</td>
                </tr>
              ))}
              {!identityRows.length ? <tr><td colSpan="5">{t.admin.gpuNoneYet}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </AdminSection>

      {onBudget ? (
        <AdminSection id="envoy-gpu-budget" title={t.admin.gpuBudgetTitle} subtitle={t.admin.gpuBudgetHint} collapsed>
          <div className="admin-form-row">
            <label className="admin-field">{t.admin.aiGatewayIdentity}
              <select className="form-input" value={chosen} onChange={(event) => setIdentity(event.target.value)}>
                {(gpu.known_identities || []).map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <label className="admin-field">{t.admin.gpuEngine}
              <select className="form-input" value={model} onChange={(event) => setBudgetModel(event.target.value)}>
                {(gpu.models || []).map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}
              </select>
            </label>
            <label className="admin-field">{t.admin.aiGatewayTokenBudget}
              <input className="form-input" type="number" min="1" value={tokens} onChange={(event) => setTokens(event.target.value)} placeholder="5000000" />
            </label>
            <button type="button" className="homepage-button-primary" disabled={saving || !chosen || !tokens}
              onClick={() => onBudget({ scope: 'identity', identity: chosen, model, tokens: Number(tokens) })}>{t.admin.gpuBudgetSet}</button>
          </div>
        </AdminSection>
      ) : null}
    </>
  );
};

// The orchestrator's usage ledger: tokens and provider-reported cost per person, model and day.
export const UsageLedgerPanel = ({ usage, t, note = null }) => {
  const o = t.admin.ops || {};
  const byCost = (left, right) => (right[1].costUsd - left[1].costUsd) || (right[1].totalTokens - left[1].totalTokens);
  const people = Object.entries(usage?.byUser || {}).sort(byCost);
  const models = Object.entries(usage?.byModel || {}).sort(byCost);
  const days = (usage?.byDay || []).slice().reverse().map((entry) => [entry.day, entry.usage || {}]);
  const totals = usage?.totals || {};
  return (
    <div className="space-y-4">
      <FactGrid items={[
        [t.admin.companyUsageCalls, formatInteger(totals.requests)],
        [o.promptTokens, formatInteger(totals.promptTokens)],
        [o.completionTokens, formatInteger(totals.completionTokens)],
        [t.admin.companyUsageCost, totals.costReportedRequests ? formatUsd(totals.costUsd) : t.admin.companyCostNotReported],
      ]} />
      <LedgerTable title={o.usageByPerson} nameLabel={t.admin.companyUsagePerson} rows={people} t={t} empty={t.admin.companyUsageEmpty} />
      <LedgerTable title={o.usageByModel} nameLabel={o.model} rows={models} t={t} />
      <LedgerTable title={o.usageByDay} nameLabel={o.day} rows={days} t={t} />
      {note}
    </div>
  );
};

// The orchestrator's activity feed, newest first; the newest fifty stand on the page, the rest on request.
export const ActivityLog = ({ activity, t, formatTimestamp, withSource = false }) => {
  const [shown, more] = useShowMore(50);
  return (
    <div className="space-y-2">
      <div className="homepage-muted-panel p-4 admin-log">
        {activity.length ? activity.slice(0, shown).map((entry, index) => {
          const parts = [formatTimestamp(entry.timestamp)];
          if (withSource) parts.push(`${t.admin.veridionActivitySource}: ${humanizeToken(entry.source)}`);
          parts.push(
            `${t.admin.veridionActivitySurface}: ${humanizeToken(entry.surface)}`,
            `${t.admin.veridionActivityEvent}: ${humanizeToken(entry.eventType)}`,
            `${t.admin.veridionActivityStatus}: ${humanizeToken(entry.status)}`,
          );
          if (entry.toolName) parts.push(`${t.admin.veridionActivityTool}: ${entry.toolName}`);
          if (entry.summary) parts.push(`${t.admin.veridionActivitySummary}: ${entry.summary}`);
          return <div key={`${entry.requestId || 'request'}-${entry.eventType || 'event'}-${index}`}>{parts.join(' | ')}</div>;
        }) : <div className="text-text-secondary">{t.admin.veridionActivityEmpty}</div>}
      </div>
      <ShowMore shown={shown} total={activity.length} onMore={more} />
    </div>
  );
};

// Label and value pairs as rows in two columns: a number stands in a row, never in a tile (IDD D-HP-ADMIN-040).
export const FactGrid = ({ items }) => (
  <dl className="admin-facts">
    {items.filter((entry) => Array.isArray(entry) && entry[0]).map(([label, value]) => (
      <div key={label} className="admin-fact">
        <dt>{label}</dt>
        <dd>{value === null || value === undefined || value === '' ? '-' : value}</dd>
      </div>
    ))}
  </dl>
);

// A line that leads to the one page that carries a figure, so no page repeats it.
export const AdminLink = ({ tab, section = '', detail = '', children }) => (
  <button type="button" className="admin-link" onClick={() => writeAdminRoute(tab, section, detail)}>{children}</button>
);

// How an orchestrator instance runs, as its health route reports it.
export const OrchestratorRuntime = ({ health, t }) => {
  const o = t.admin.ops || {};
  const llm = health?.llm || {};
  return (
    <FactGrid items={[
      [o.executionMode, llm.executionMode],
      [o.providerProtocol, llm.protocol],
      [o.contextWindow, llm.configuredContextWindow ? formatInteger(llm.configuredContextWindow) : null],
      [o.requestTimeout, llm.requestTimeoutSeconds ? `${llm.requestTimeoutSeconds} s` : null],
      [o.diagnostics, health ? (health.diagnostics ? o.diagnosticsOn : o.diagnosticsOff) : null],
      [o.externalServers, health ? `${formatInteger(health.connectedExternalServerCount)} / ${formatInteger(health.externalServerCount)}` : null],
      [t.admin.veridionRuntimeProfiles, (health?.runtimeProfiles || []).join(', ')],
      [o.mcpEndpoint, health?.mcpEndpoint],
    ]} />
  );
};
